import { LoginPopupState, PopupTypes } from "./../types/Popup";
import { IColorPickerResult } from "../../libs/Picker/types";
import { ActionType, PartialPick } from "../types/store-types";
import { CurrentField, CurrentInputEditing } from "../fields/types";

/* - Data Types - */

export enum SettingsSections {
    GENERAL,
    LIMITS,
    NOTIFICATION,
    SAVE_PROGRESS,
    CONFIRMATION,
}

export interface ISettingsData {
    isOpen: boolean;
    currentSection: SettingsSections;
}

export interface ICollaborationData {
    isOpen: boolean;
}

export interface IUserOptionsData {
    isOpen: boolean;
}

export interface ISideBarData {
    isOpen: boolean;
}

export interface ILogicPopup {
    isOpen: boolean;
    diffX: number;
    diffY: number;
    leftPosition: number;
    topPosition: number;
    currentRuleIndex: number;
}

export type ChangeFormBackgroundDataType = IColorPickerResult;

export interface IChangeFormBackgroundStyleVisibilityData {
    isVisible: boolean;
}

export interface IMapScriptStatus {
    isLoaded: boolean;
}

export interface IAuthPopUpRender {
    authPopUpRender: null | boolean;
}

export interface IChangePadesViewModeData {}

export type ICurrentPopupData = PopupTypes | null;
export type ICurrentLoginPopupState = LoginPopupState | null;

export interface IColorData {
    isStyleVisible: boolean;
    solid: string | null;
    gradient: string | null;
    imageUrl: string | null;
    lastModified: IColorPickerResult;
}
export interface IUIInitData {
    isVerticalView: boolean;
    isPaginationActive: boolean;
}

export type ChangeLogicPopupDataType = PartialPick<
    ILogicPopup,
    "isOpen" | "diffX" | "diffY" | "leftPosition" | "topPosition" | "currentRuleIndex"
>;
export interface IChangePagesViewModeData {
    isVerticalView: boolean;
}

export interface IChangeIsPaginationActiveData {
    isPaginationActive: boolean;
}

export enum DeviceType {
    DESKTOP,
    TABLET,
    MOBILE,
}
/* - State - */

export interface IUIState {
    settings: ISettingsData;
    collaboration: ICollaborationData;
    userOptions: IUserOptionsData;
    currentPopup: ICurrentPopupData;
    currentLoginPopupState: ICurrentLoginPopupState;
    currentField: CurrentField;
    currentInputEditing: CurrentInputEditing;
    isVerticalView: boolean;
    authPopUpRender: boolean | null;
    isPaginationActive: boolean;
    sideBar: ISideBarData;
    formBackground: IColorData;
    logicPopup: ILogicPopup;
    mapScriptStatus: boolean;
    viewMode: DeviceType;
}

/* - Action Types - */

export enum UIActionTypes {
    CHANGE_VIEW_MODE = "UI.CHANGE_VIEW_MODE",
    CHANGE_SETTINGS = "UI.CHANGE_SETTINGS",
    CHANGE_AUTH_POPUP = "UI.CHANGE_AUTH_POPUP",
    CHANGE_COLLABORATION = "UI.CHANGE_COLLABORATION",
    CHANGE_USER_OPTIONS = "UI.CHANGE_USER_OPTIONS",
    CHANGE_CURRENT_POPUP = "UI.CHANGE_CURRENT_POPUP",
    CHANGE_CURRENT_LOGIN_POPUP_STATE = "UI.CHANGE_CURRENT_LOGIN_POPUP_STATE",
    CHANGE_CURRENT_FIELD = "UI.CHANGE_CURRENT_FIELD",
    CHANGE_INPUT_EDITTING = "UI.CHANGE_INPUT_EDITING",
    CHANGE_SIDEBAR = "UI.CHANGE_SIDEBAR",
    CHANGE_PAGE_VIEW_MODE_REQUEST = "UI.CHANGE_PAGE_VIEW_MODE_REQUEST",
    CHANGE_PAGE_VIEW_MODE_SUCCESS = "UI.CHANGE_PAGE_VIEW_MODE_SUCCESS",
    CHANGE_PAGE_VIEW_MODE_FAILURE = "UI.CHANGE_PAGE_VIEW_MODE_FAILURE",
    CHANGE_FORM_BACKGROUND = "UI.CHANGE_FORM_BACKGROUND",
    CHANGE_FORM_BACKGROUND_VISIBILITY = "UI.CHANGE_FORM_BACKGROUND_VISIBILITY",
    CHANGE_PAGINATION_ACTIVE_STATUS_REQUEST = "UI.CHANGE_PAGINATION_ACTIVE_STATUS_REQUEST",
    CHANGE_PAGINATION_ACTIVE_STATUS_SUCCESS = "UI.CHANGE_PAGINATION_ACTIVE_STATUS_SUCCSESS",
    CHANGE_PAGINATION_ACTIVE_STATUS_FAILURE = "UI.CHANGE_PAGINATION_ACTIVE_STATUS_FAILURE",
    CHANGE_LOGIC_POPUP = "UI.CHANGE_LOGIC_POPUP",
    CHANGE_MAP_SCRIPT_STATUS = "UI.CHANGE_MAP_SCRIPT_STATUS",
    INIT_UI = "UI.INIT_UI",
}

/* - Action Interfaces  - */
export type ChangeViewModeActionType = ActionType<UIActionTypes.CHANGE_VIEW_MODE, DeviceType>;

export type ChangeSettingsActionType = ActionType<UIActionTypes.CHANGE_SETTINGS, Partial<ISettingsData>>;

export type ChangeCollaborationActionType = ActionType<UIActionTypes.CHANGE_COLLABORATION, ICollaborationData>;

export type ChangeUserOptionsActionType = ActionType<UIActionTypes.CHANGE_USER_OPTIONS, IUserOptionsData>;

export type ChangeCurrentPopupActionType = ActionType<UIActionTypes.CHANGE_CURRENT_POPUP, ICurrentPopupData>;

export type ChangeCurrentLoginPopupStateActionType = ActionType<
    UIActionTypes.CHANGE_CURRENT_LOGIN_POPUP_STATE,
    ICurrentLoginPopupState
>;

export type ChangeCurrentFieldActionType = ActionType<UIActionTypes.CHANGE_CURRENT_FIELD, CurrentField>;

export type ChangeInputEditingActionType = ActionType<UIActionTypes.CHANGE_INPUT_EDITTING, CurrentInputEditing>;

export type ChangeSideBarActionType = ActionType<UIActionTypes.CHANGE_SIDEBAR, ISideBarData>;

export type ChangePagesViewModeRequestActionType = ActionType<
    UIActionTypes.CHANGE_PAGE_VIEW_MODE_REQUEST,
    IChangePagesViewModeData
>;
export type ChangePagesViewModeSuccessActionType = ActionType<
    UIActionTypes.CHANGE_PAGE_VIEW_MODE_SUCCESS,
    IChangePagesViewModeData
>;
export type ChangePagesViewModeFailureActionType = ActionType<
    UIActionTypes.CHANGE_PAGE_VIEW_MODE_FAILURE,
    IChangePagesViewModeData
>;

export type ChangeIsPaginationActiveRequestActionType = ActionType<
    UIActionTypes.CHANGE_PAGINATION_ACTIVE_STATUS_REQUEST,
    IChangeIsPaginationActiveData
>;
export type ChangeIsPaginationActiveSuccessActionType = ActionType<
    UIActionTypes.CHANGE_PAGINATION_ACTIVE_STATUS_SUCCESS,
    IChangeIsPaginationActiveData
>;
export type ChangeIsPaginationActiveFailureActionType = ActionType<
    UIActionTypes.CHANGE_PAGINATION_ACTIVE_STATUS_FAILURE,
    IChangeIsPaginationActiveData
>;

export type InitUIActionType = ActionType<UIActionTypes.INIT_UI, IUIInitData>;

export type ChangeFormBackgroundActionType = ActionType<
    UIActionTypes.CHANGE_FORM_BACKGROUND,
    ChangeFormBackgroundDataType
>;

export type ChangeFormBackgroundStyleVisibilityActionType = ActionType<
    UIActionTypes.CHANGE_FORM_BACKGROUND_VISIBILITY,
    IChangeFormBackgroundStyleVisibilityData
>;

export type ChangeLogicPopupActionType = ActionType<UIActionTypes.CHANGE_LOGIC_POPUP, ChangeLogicPopupDataType>;

export type ChangeMapScriptStatusActionType = ActionType<UIActionTypes.CHANGE_MAP_SCRIPT_STATUS, IMapScriptStatus>;

export type ChangeAuthPopUpRenderActionType = ActionType<UIActionTypes.CHANGE_AUTH_POPUP, IAuthPopUpRender>;
